import { useState, useMemo } from 'react';
import { Tag, Typography, Flex, Dropdown, Tree, Button, message } from 'antd';
import { CheckIcon, PlusIcon, SpinnerGapIcon, XIcon } from '@phosphor-icons/react';
import styled from '@emotion/styled';
import { useUpdateWorkflowItemMutation } from '../../../../../store/workflows/workflowApi';
import { TreeSelectDataNode } from './types';
import { handleTreeCheck } from './utils';

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const AddTagButton = styled(Button)`
  padding: 0 16px;
  height: 24px;
  border: none;
  background: none;
  color: #3132eb;
  font-size: 12px;
  font-weight: 500;
  box-shadow: none;
  width: min-content;

  &:hover {
    color: #171717;
    background: #f4f4f4;
  }
`;

interface ITagsForQCProps {
  workflowId: number;
  runId: number;
  itemId: number;
  proposedTags: Array<string>;
  acceptedTags: Array<string>;
  rejectedTags: Array<string>;
  allTagOptions: Array<TreeSelectDataNode>;
}

const TagsForQC = ({
  workflowId,
  runId,
  itemId,
  proposedTags,
  acceptedTags,
  rejectedTags,
  allTagOptions,
}: ITagsForQCProps) => {
  const initialSelected = useMemo(
    () => Array.from(new Set([...proposedTags, ...acceptedTags])),
    [proposedTags, acceptedTags],
  );
  const [selectedTags, setSelectedTags] = useState<Array<string>>(initialSelected);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [updateWorkflowItem, { isLoading }] = useUpdateWorkflowItemMutation();

  const handleAccept = async () => {
    const newAccepted = selectedTags;
    const rejected = proposedTags.filter(tag => !selectedTags.includes(tag));
    try {
      await updateWorkflowItem({
        workflowId,
        runId,
        itemId,
        acceptedTags: newAccepted,
        rejectedTags: rejected,
        proposedTags: [],
      }).unwrap();
      message.success(`Tags ${newAccepted.join(', ')} accepted!`);
    } catch (e) {
      message.error('Failed to update tags');
    }
  };

  const handleReject = async () => {
    const rejected = Array.from(new Set([...proposedTags, ...selectedTags, ...rejectedTags]));
    const updatedAcceptedTags = acceptedTags.filter(tag => !rejected.includes(tag));
    try {
      await updateWorkflowItem({
        workflowId,
        runId,
        itemId,
        acceptedTags: updatedAcceptedTags,
        rejectedTags: rejected,
        proposedTags: [],
      }).unwrap();
      message.success(`Tags ${rejected.join(', ')} Rejected!`);
    } catch (e) {
      message.error('Failed to update tags');
    }
  };

  const handleTagClose = (tag: string) => {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  };

  return (
    <Flex flex={1} justify="space-between" align="center">
      <Flex vertical gap={8}>
        <Typography.Text style={{ fontWeight: 400, fontSize: 12 }}>Tags</Typography.Text>
        <Flex vertical gap={4}>
          <TagsWrapper>
            {selectedTags.map(tag => (
              <Tag
                key={tag}
                closable
                onClose={e => {
                  e.preventDefault();
                  handleTagClose(tag);
                }}
                style={{
                  color: '#171717',
                  background: '#F5E7FF',
                  fontWeight: 700,
                  borderRadius: 20,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  fontSize: 11,
                  opacity: 0.8,
                }}
              >
                {tag}
              </Tag>
            ))}
          </TagsWrapper>
          <Flex gap={8}>
            <Dropdown
              open={dropdownOpen}
              onOpenChange={setDropdownOpen}
              popupRender={() => (
                <div
                  style={{
                    padding: 8,
                    background: '#fff',
                    borderRadius: 20,
                    boxShadow: '0px 4px 34px 0px rgba(0, 0, 0, 0.1)',
                    maxHeight: 300,
                    overflowY: 'auto',
                  }}
                >
                  <Tree
                    checkable
                    selectable={false}
                    checkStrictly={true}
                    treeData={allTagOptions}
                    checkedKeys={selectedTags}
                    onCheck={checked => {
                      const result = handleTreeCheck(checked, allTagOptions);
                      setSelectedTags(result);
                    }}
                    defaultExpandAll
                  />
                </div>
              )}
              trigger={['click']}
              arrow
            >
              <AddTagButton
                type="text"
                icon={<PlusIcon />}
                onClick={event => {
                  setDropdownOpen(true);
                  event.stopPropagation();
                }}
                loading={isLoading}
              >
                ADD
              </AddTagButton>
            </Dropdown>
          </Flex>
        </Flex>
      </Flex>
      <Flex gap={8} align="center">
        <Button
          onClick={handleAccept}
          variant="outlined"
          color="blue"
          disabled={selectedTags.length === 0}
          style={{
            borderRadius: 10,
            minWidth: 0,
            width: 48,
            height: 48,
            aspectRatio: '1 / 1',
            padding: 0,
            flexShrink: 0,
          }}
        >
          {isLoading ? (
            <SpinnerGapIcon className="anticon-spin" />
          ) : (
            <Flex vertical gap={4} align="center">
              <CheckIcon />
              <span style={{ fontSize: 10 }}>Accept</span>
            </Flex>
          )}
        </Button>
        <Button
          onClick={handleReject}
          variant="outlined"
          color="danger"
          disabled={selectedTags.length === 0}
          style={{
            borderRadius: 10,
            minWidth: 0,
            width: 48,
            height: 48,
            aspectRatio: '1 / 1',
            padding: 0,
            flexShrink: 0,
          }}
        >
          {isLoading ? (
            <SpinnerGapIcon className="anticon-spin" />
          ) : (
            <Flex vertical gap={4} align="center">
              <XIcon />
              <span style={{ fontSize: 10 }}>Reject</span>
            </Flex>
          )}
        </Button>
      </Flex>
    </Flex>
  );
};

export default TagsForQC;
